import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router';

import { colors } from '../../theme/tokens';

interface Props {
  tenantName: string | undefined;
  tenantCode: string | undefined;
  onClose?: () => void;
  /** When true, the "Change" affordance is hidden (bare-chrome single-tenant users). */
  hideChange?: boolean;
}

/**
 * Brand header at the top of the sidebar. Shows the S logo, tenant name +
 * code, and a "Change" link that returns to the tenant picker. On mobile
 * also renders the drawer close button.
 */
const BrandHeader: React.FC<Props> = ({ tenantName, tenantCode, onClose, hideChange = false }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => navigate('/')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/');
          }
        }}
        aria-label="Go to tenant picker"
        sx={{
          width: 48,
          height: 48,
          borderRadius: '8px',
          backgroundColor: colors.brand,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 700,
          fontSize: 20,
          flexShrink: 0,
          cursor: 'pointer',
          '&:focus-visible': {
            outline: `2px solid ${colors.brandHover}`,
            outlineOffset: 2,
          },
        }}
      >
        S
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: colors.textPrimary,
            fontFamily: 'Manrope, sans-serif',
          }}
          noWrap
        >
          {tenantName || 'Loading...'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            sx={{ fontSize: 14, color: colors.textSecondary, fontFamily: 'Manrope, sans-serif' }}
            noWrap
          >
            {tenantCode}
          </Typography>
          {!hideChange && (
            <Typography
              component="button"
              type="button"
              onClick={() => navigate('/')}
              sx={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                fontFamily: 'inherit',
                fontSize: 12,
                color: colors.brand,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                '&:focus-visible': { outline: `2px solid ${colors.brandHover}`, outlineOffset: 2 },
              }}
            >
              Change
            </Typography>
          )}
        </Box>
      </Box>
      {onClose && (
        <IconButton onClick={onClose} aria-label="Close navigation" sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default BrandHeader;
